import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import ReceiptModal from './ReceiptModal';
import { Users, Plus, Trash2, ShieldCheck, Info, CheckCircle } from 'lucide-react';

interface ClientManageModalProps {
    client: any;
    onClose: () => void;
    onUpdated?: () => void;
}

const ClientManageModal = ({ client, onClose, onUpdated }: ClientManageModalProps) => {
    const [activeTab, setActiveTab] = useState<'subscription' | 'history' | 'reminders'>('subscription');
    const [receiptData, setReceiptData] = useState<any>(null);
    
    // Data states
    const [subscriptionTypes, setSubscriptionTypes] = useState<any[]>([]);
    const [forfaitsAbonnement, setForfaitsAbonnement] = useState<any[]>([]);
    const [forfaitsSeance, setForfaitsSeance] = useState<any[]>([]);
    const [clientSubscriptions, setClientSubscriptions] = useState<any[]>([]);
    
    // Service Type toggle: 'subscription' | 'seance'
    const [serviceType, setServiceType] = useState<'subscription' | 'seance'>('subscription');

    // Form states - Forfait Abonnement
    const [selectedForfaitAbId, setSelectedForfaitAbId] = useState('');
    const [subTypeId, setSubTypeId] = useState('');
    const [subPriceId, setSubPriceId] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [quantity, setQuantity] = useState(1);
    
    // Form states - Forfait Séance
    const [selectedForfaitScId, setSelectedForfaitScId] = useState('');
    const [seanceQuantity, setSeanceQuantity] = useState(1);
    const [seanceDate, setSeanceDate] = useState(new Date().toISOString().split('T')[0]);

    // Form states - Prolongation
    const [extendingSubId, setExtendingSubId] = useState<number | null>(null);
    const [extendQuantity, setExtendQuantity] = useState(1);
    
    // Form states - Mode de paiement (Espèces par défaut)
    const [paymentMethod, setPaymentMethod] = useState('Espèces');

    // Fetch initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [resSubs, resForfAb, resForfSc] = await Promise.all([
                    api.get('/subscriptions/types'),
                    api.get('/forfaits/abonnements?active_only=true'),
                    api.get('/forfaits/seances?active_only=true')
                ]);

                setSubscriptionTypes(resSubs.data || []);
                if (resSubs.data && resSubs.data.length > 0) {
                    setSubTypeId(resSubs.data[0].id.toString());
                }

                const forfAbList = resForfAb.data || [];
                setForfaitsAbonnement(forfAbList);
                if (forfAbList.length > 0) {
                    setSelectedForfaitAbId(forfAbList[0].id.toString());
                }

                const forfScList = resForfSc.data || [];
                setForfaitsSeance(forfScList);
                if (forfScList.length > 0) {
                    setSelectedForfaitScId(forfScList[0].id.toString());
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchInitialData();
        fetchClientHistory();
    }, [client.id]);

    const fetchClientHistory = async () => {
        try {
            const res = await api.get(`/subscriptions/client/${client.id}`);
            setClientSubscriptions(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    // Memoized selection for Forfait Abonnement & Séance
    const selectedForfait = useMemo(() => {
        if (forfaitsAbonnement.length === 0) return null;
        return forfaitsAbonnement.find(f => String(f.id) === String(selectedForfaitAbId)) || forfaitsAbonnement[0];
    }, [forfaitsAbonnement, selectedForfaitAbId]);

    const isPackEntreprise = Boolean(
        (selectedForfait?.max_members && Number(selectedForfait.max_members) > 1) ||
        selectedForfait?.name?.toLowerCase().includes('pack entreprise')
    );
    const maxAdditionalAllowed = 4; // 1 souscripteur + 4 personnes additionnelles = 5 max
    const [additionalPackMembers, setAdditionalPackMembers] = useState<Array<{ full_name: string; phone: string }>>([]);

    const selectedSeanceForfait = useMemo(() => {
        if (forfaitsSeance.length === 0) return null;
        return forfaitsSeance.find(s => String(s.id) === String(selectedForfaitScId)) || forfaitsSeance[0];
    }, [forfaitsSeance, selectedForfaitScId]);

    // Legacy support
    const selectedSub = subscriptionTypes.find(t => t.id.toString() === subTypeId);
    const selectedPrice = selectedSub?.prices?.find((p: any) => p.id.toString() === subPriceId);
    
    useEffect(() => {
        if (selectedSub && selectedSub.prices && selectedSub.prices.length > 0) {
            setSubPriceId(selectedSub.prices[0].id.toString());
        } else {
            setSubPriceId('');
        }
    }, [subTypeId, selectedSub]);

    // Calculate end date based on forfait periodicity and quantity
    useEffect(() => {
        if (!startDate) return;
        const start = new Date(startDate);
        let end = new Date(start);
        const q = quantity || 1;

        if (selectedForfait) {
            switch (selectedForfait.periodicity) {
                case 'mensuel':
                    end.setMonth(end.getMonth() + q);
                    break;
                case 'trimestriel':
                    end.setMonth(end.getMonth() + (3 * q));
                    break;
                case 'semestriel':
                    end.setMonth(end.getMonth() + (6 * q));
                    break;
                case 'annuel':
                    end.setFullYear(end.getFullYear() + q);
                    break;
                case 'hebdomadaire':
                    end.setDate(end.getDate() + (7 * q));
                    break;
                default:
                    end.setMonth(end.getMonth() + q);
                    break;
            }
        } else if (selectedPrice) {
            switch (selectedPrice.periodicity) {
                case 'seance':
                    end.setDate(end.getDate() + q);
                    break;
                case 'mensuel':
                    end.setMonth(end.getMonth() + q);
                    break;
                case 'trimestriel':
                    end.setMonth(end.getMonth() + (3 * q));
                    break;
                case 'semestriel':
                    end.setMonth(end.getMonth() + (6 * q));
                    break;
                case 'annuel':
                    end.setFullYear(end.getFullYear() + q);
                    break;
            }
        }
        
        setEndDate(end.toISOString().split('T')[0]);
    }, [startDate, selectedForfait, selectedPrice, quantity]);

    // Dernier abonnement connu du client
    const lastSub = useMemo(() => {
        if (!clientSubscriptions || clientSubscriptions.length === 0) return null;
        return [...clientSubscriptions].sort((a, b) => String(b.end_date || '').localeCompare(String(a.end_date || '')))[0];
    }, [clientSubscriptions]);

    // Présélectionner automatiquement le dernier forfait suivi par le client
    useEffect(() => {
        if (lastSub?.forfait_abonnement_id && forfaitsAbonnement.some(f => String(f.id) === String(lastSub.forfait_abonnement_id))) {
            setSelectedForfaitAbId(String(lastSub.forfait_abonnement_id));
        }
    }, [lastSub, forfaitsAbonnement]);

    // Vérifier si le client conserve le même forfait
    const isSameForfait = useMemo(() => {
        if (!lastSub || !selectedForfait) return false;
        if (lastSub.forfait_abonnement_id && String(lastSub.forfait_abonnement_id) === String(selectedForfait.id)) {
            return true;
        }
        if (lastSub.type_name && selectedForfait.name && lastSub.type_name.toLowerCase().trim() === selectedForfait.name.toLowerCase().trim()) {
            return true;
        }
        return false;
    }, [lastSub, selectedForfait]);

    // Vérifier si l'inscription annuelle du client est encore valide (< 365 jours)
    const isAnnualValid = useMemo(() => {
        const refDate = client?.last_inscription_date || lastSub?.start_date || lastSub?.created_at;
        if (!refDate) return false;
        const ref = new Date(refDate);
        const now = new Date();
        const diffDays = (now.getTime() - ref.getTime()) / (1000 * 60 * 60 * 24);
        return diffDays <= 365;
    }, [client, lastSub]);

    // État pour savoir si les frais d'inscription doivent être appliqués
    const [includeRegistrationFee, setIncludeRegistrationFee] = useState<boolean>(false);

    // RÈGLE MÉTIER : Frais d'inscription annuels.
    // Ne s'appliquent PAS à chaque réabonnement, SAUF si le client change de forfait (ou que son inscription > 1 an).
    useEffect(() => {
        if (!selectedForfait || Number(selectedForfait.registration_fee || 0) === 0) {
            setIncludeRegistrationFee(false);
            return;
        }
        if (lastSub) {
            if (isSameForfait && isAnnualValid) {
                // Même forfait et annuel encore valide : Pas de frais d'inscription
                setIncludeRegistrationFee(false);
            } else {
                // Changement de forfait OU annuel expiré : Frais d'inscription appliqués
                setIncludeRegistrationFee(true);
            }
        } else {
            // Premier abonnement
            setIncludeRegistrationFee(true);
        }
    }, [selectedForfait, lastSub, isSameForfait, isAnnualValid]);

    // Frais d'inscription calculés
    const applicableRegFee = (includeRegistrationFee && selectedForfait) ? Number(selectedForfait.registration_fee || 0) : 0;
    const seanceRegFee = selectedSeanceForfait ? Number(selectedSeanceForfait.registration_fee || 0) : 0;

    // Prix calculé pour l'abonnement
    const currentPrice = selectedForfait
        ? (Number(selectedForfait.price) * (quantity || 1)) + applicableRegFee
        : selectedPrice
            ? (Number(selectedPrice.price_without_inscription) * (quantity || 1))
            : 0;

    // Calculated price for Seance
    const currentSeancePrice = selectedSeanceForfait
        ? (Number(selectedSeanceForfait.price) * (seanceQuantity || 1)) + seanceRegFee
        : 0;
    
    // Assign Abonnement
    const handleAssignSubscription = async (e: React.FormEvent) => {
        e.preventDefault();
        const activeForfait = selectedForfait;
        if (!activeForfait && !subPriceId) {
            alert("Veuillez sélectionner un forfait d'abonnement.");
            return;
        }
        try {
            const cleanMembers = isPackEntreprise
                ? [
                    {
                        full_name: `${client.first_name} ${client.last_name}`.trim(),
                        phone: (client.phone || '').trim(),
                        role: 'Souscripteur principal'
                    },
                    ...additionalPackMembers
                        .map(m => ({ full_name: m.full_name.trim(), phone: m.phone.trim() }))
                        .filter(m => m.full_name.length > 0)
                        .slice(0, maxAdditionalAllowed)
                ]
                : [];

            const payload: any = {
                client_id: client.id,
                start_date: startDate,
                end_date: endDate || null,
                pays_inscription: applicableRegFee > 0,
                quantity: quantity,
                payment_method: paymentMethod || 'Espèces',
                group_members: cleanMembers.length > 0 ? cleanMembers : null
            };

            if (activeForfait) {
                payload.forfait_abonnement_id = activeForfait.id;
            } else {
                payload.subscription_price_id = parseInt(subPriceId);
            }

            await api.post('/subscriptions', payload);
            alert("Abonnement souscrit et paiement enregistré avec succès !");
            await fetchClientHistory();
            if (onUpdated) onUpdated();
            setActiveTab('history');
            
            // Ouvrir le reçu
            setReceiptData({
                type: 'subscription',
                data: {
                    clientName: `${client.first_name} ${client.last_name}`,
                    clientPhone: client.phone,
                    planName: activeForfait ? activeForfait.name : selectedSub?.name,
                    periodicity: activeForfait ? activeForfait.periodicity : selectedPrice?.periodicity,
                    hasInscription: applicableRegFee > 0,
                    registration_fee: applicableRegFee,
                    paymentMethod: paymentMethod || 'Espèces',
                    total: currentPrice
                }
            });
        } catch (error) {
            console.error(error);
            alert("Erreur lors de l'assignation de l'abonnement");
        }
    };

    // Assign Séance
    const handleAssignSeance = async (e: React.FormEvent) => {
        e.preventDefault();
        const activeSeanceForfait = selectedSeanceForfait;
        if (!activeSeanceForfait) {
            alert("Veuillez sélectionner un forfait séance.");
            return;
        }
        try {
            await api.post('/subscriptions', {
                client_id: client.id,
                forfait_seance_id: activeSeanceForfait.id,
                start_date: seanceDate,
                end_date: seanceDate,
                quantity: seanceQuantity,
                payment_method: paymentMethod || 'Espèces'
            });
            alert("Séance enregistrée et paiement validé avec succès !");
            await fetchClientHistory();
            if (onUpdated) onUpdated();
            setActiveTab('history');

            setReceiptData({
                type: 'subscription',
                data: {
                    clientName: `${client.first_name} ${client.last_name}`,
                    planName: activeSeanceForfait.name,
                    periodicity: 'Séance unique',
                    hasInscription: seanceRegFee > 0,
                    registration_fee: seanceRegFee,
                    paymentMethod: paymentMethod || 'Espèces',
                    total: currentSeancePrice
                }
            });
        } catch (error) {
            console.error(error);
            alert("Erreur lors de l'enregistrement de la séance");
        }
    };

    const handleExtend = async (sub: any) => {
        try {
            const start = new Date(sub.end_date);
            let end = new Date(start);
            const q = extendQuantity;

            switch (sub.periodicity) {
                case 'seance': end.setDate(end.getDate() + q); break;
                case 'mensuel': end.setMonth(end.getMonth() + q); break;
                case 'trimestriel': end.setMonth(end.getMonth() + (3 * q)); break;
                case 'semestriel': end.setMonth(end.getMonth() + (6 * q)); break;
                case 'annuel': end.setFullYear(end.getFullYear() + q); break;
                case 'hebdomadaire': end.setDate(end.getDate() + (7 * q)); break;
                default: end.setMonth(end.getMonth() + q); break;
            }

            const unit_price = Number(sub.price_without_inscription || sub.price_with_inscription || 0);
            const added_price = unit_price * q;
            const new_end_date = end.toISOString().split('T')[0];

            await api.put(`/subscriptions/${sub.id}/extend`, {
                new_end_date,
                added_price,
                payment_method: paymentMethod || 'Espèces'
            });
            
            alert(`Abonnement prolongé et paiement de ${added_price.toLocaleString()} FCFA enregistré avec succès !`);
            setExtendingSubId(null);
            setExtendQuantity(1);
            await fetchClientHistory();
            if (onUpdated) onUpdated();
            setActiveTab('history');
            
            setReceiptData({
                type: 'subscription',
                data: {
                    clientName: `${client.first_name} ${client.last_name}`,
                    planName: sub.type_name,
                    periodicity: sub.periodicity,
                    paymentMethod: paymentMethod || 'Espèces',
                    total: added_price
                }
            });
        } catch (error) {
            console.error(error);
            alert("Erreur lors de la prolongation.");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Gérer le client : {client.first_name} {client.last_name}</h3>
                        <p className="text-sm text-gray-500">
                            {client.last_inscription_date ? `Dernière inscription: ${new Date(client.last_inscription_date).toLocaleDateString('fr-FR')}` : "Aucune inscription payée"}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>
                </div>
                
                <div className="flex border-b border-gray-200 shrink-0 overflow-x-auto bg-gray-50/50">
                    <button onClick={() => setActiveTab('subscription')} className={`px-5 py-3 text-sm font-bold whitespace-nowrap transition-colors ${activeTab === 'subscription' ? 'border-b-2 border-brand-primary text-brand-dark bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'}`}>Souscription</button>
                    <button onClick={() => setActiveTab('history')} className={`px-5 py-3 text-sm font-bold whitespace-nowrap transition-colors ${activeTab === 'history' ? 'border-b-2 border-brand-primary text-brand-dark bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'}`}>Historique</button>
                    <button onClick={() => setActiveTab('reminders')} className={`px-5 py-3 text-sm font-bold whitespace-nowrap transition-colors ${activeTab === 'reminders' ? 'border-b-2 border-brand-primary text-brand-dark bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'}`}>Relances</button>
                </div>

                <div className="p-6 overflow-y-auto">
                    {activeTab === 'subscription' && (
                        <div className="space-y-4">
                            <div className="flex gap-4 border-b border-gray-200 pb-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="serviceType" value="subscription" checked={serviceType === 'subscription'} onChange={() => setServiceType('subscription')} className="text-brand-primary focus:ring-brand-primary" />
                                    <span className="text-sm font-bold text-gray-800">Abonnement</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="serviceType" value="seance" checked={serviceType === 'seance'} onChange={() => setServiceType('seance')} className="text-brand-primary focus:ring-brand-primary" />
                                    <span className="text-sm font-bold text-gray-800">Séance Unique</span>
                                </label>
                            </div>

                            {/* --- OPTION 1 : FORFAIT ABONNEMENT --- */}
                            {serviceType === 'subscription' && (
                                <form onSubmit={handleAssignSubscription} className="space-y-4">
                                    {forfaitsAbonnement.length > 0 ? (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Choisir un forfait d'abonnement
                                            </label>
                                            <select 
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary font-medium bg-white"
                                                value={selectedForfaitAbId || (selectedForfait ? selectedForfait.id : '')}
                                                onChange={(e) => setSelectedForfaitAbId(e.target.value)}
                                                required
                                            >
                                                {forfaitsAbonnement.map(f => (
                                                    <option key={f.id} value={f.id}>
                                                        {f.name} — {Number(f.price).toLocaleString()} FCFA ({f.duration} / {f.periodicity})
                                                    </option>
                                                ))}
                                            </select>
                                            {selectedForfait?.description && (
                                                <p className="mt-1.5 text-xs text-gray-500 italic bg-gray-50 p-2 rounded">
                                                    {selectedForfait.description}
                                                </p>
                                            )}

                                            {/* Saisie Bénéficiaires si Pack Entreprise / Groupe */}
                                            {isPackEntreprise && (
                                                <div className="mt-3 p-3.5 bg-purple-50/90 border border-purple-200 rounded-xl space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-1.5 text-xs font-bold text-purple-900 uppercase">
                                                            <Users size={15} className="text-purple-600" />
                                                            <span>Bénéficiaires du Pack ({1 + additionalPackMembers.filter(m => m.full_name.trim()).length}/5 au total)</span>
                                                        </div>
                                                        {additionalPackMembers.length < maxAdditionalAllowed && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setAdditionalPackMembers([...additionalPackMembers, { full_name: '', phone: '' }])}
                                                                className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-[11px] font-bold flex items-center gap-1 cursor-pointer transition-colors"
                                                            >
                                                                <Plus size={12} />
                                                                <span>Ajouter ({additionalPackMembers.length + 1}/4 autres)</span>
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Personne 1 : Souscripteur principal */}
                                                    <div className="flex items-center justify-between gap-2 bg-white p-2.5 rounded-lg border-2 border-purple-300/80 shadow-2xs">
                                                        <div className="flex items-center gap-2.5">
                                                            <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-[11px] font-black flex items-center justify-center shrink-0">
                                                                1
                                                            </span>
                                                            <div>
                                                                <div className="text-xs font-bold text-gray-900">
                                                                    {client.first_name} {client.last_name}
                                                                </div>
                                                                <div className="text-[10px] text-gray-500 font-mono">
                                                                    {client.phone || '-'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-purple-100 text-purple-800">
                                                            ✓ Souscripteur (inclus d'office)
                                                        </span>
                                                    </div>

                                                    {/* Personnes 2 à 5 */}
                                                    <div className="space-y-2">
                                                        {additionalPackMembers.length > 0 ? (
                                                            additionalPackMembers.map((m, idx) => (
                                                                <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-purple-100">
                                                                    <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-800 text-[11px] font-bold flex items-center justify-center shrink-0">
                                                                        {idx + 2}
                                                                    </span>
                                                                    <input
                                                                        type="text"
                                                                        placeholder={`Nom & Prénoms personne ${idx + 2} *`}
                                                                        value={m.full_name}
                                                                        onChange={(e) => {
                                                                            const copy = [...additionalPackMembers];
                                                                            copy[idx].full_name = e.target.value;
                                                                            setAdditionalPackMembers(copy);
                                                                        }}
                                                                        className="flex-1 px-2.5 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-400 outline-none font-semibold"
                                                                    />
                                                                    <input
                                                                        type="tel"
                                                                        placeholder="Téléphone"
                                                                        value={m.phone}
                                                                        onChange={(e) => {
                                                                            const copy = [...additionalPackMembers];
                                                                            copy[idx].phone = e.target.value;
                                                                            setAdditionalPackMembers(copy);
                                                                        }}
                                                                        className="w-28 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-purple-400 outline-none font-mono"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setAdditionalPackMembers(additionalPackMembers.filter((_, i) => i !== idx))}
                                                                        className="p-1 text-gray-400 hover:text-red-500 rounded"
                                                                        title="Supprimer cette personne"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="p-2.5 bg-white/60 border border-dashed border-purple-200 rounded-lg text-center text-[11px] text-purple-800">
                                                                Cliquez sur <strong>« Ajouter »</strong> pour saisir jusqu'à 4 autres personnes.
                                                            </div>
                                                        )}
                                                    </div>
                                                    <p className="text-[10px] text-purple-700 italic">
                                                        1 seul badge partagé sera utilisé pour l'ensemble des membres de ce pack.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Choisir un abonnement</label>
                                            <select 
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary"
                                                value={subTypeId}
                                                onChange={(e) => setSubTypeId(e.target.value)}
                                                required
                                            >
                                                {subscriptionTypes.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                            {selectedSub && selectedSub.prices && selectedSub.prices.length > 0 && (
                                                <div className="mt-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Périodicité</label>
                                                    <select 
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary capitalize"
                                                        value={subPriceId}
                                                        onChange={(e) => setSubPriceId(e.target.value)}
                                                        required
                                                    >
                                                        {selectedSub.prices.map((p: any) => (
                                                             <option key={p.id} value={p.id}>{p.periodicity}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                                            <input 
                                                type="date" 
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary" 
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin (calculée)</label>
                                            <input 
                                                type="date" 
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary bg-gray-50 text-gray-700 font-semibold" 
                                                value={endDate}
                                                readOnly
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Quantité (x Durée)</label>
                                        <input 
                                            type="number" min="1"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary"
                                            value={quantity}
                                            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                                        <select 
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary bg-white font-medium"
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            required
                                        >
                                            <option value="Espèces">Espèces</option>
                                            <option value="Wave">Wave</option>
                                            <option value="Orange Money">Orange Money</option>
                                            <option value="MTN Money">MTN Money</option>
                                            <option value="Moov Money">Moov Money</option>
                                            <option value="Carte Bancaire">Carte Bancaire</option>
                                            <option value="Virement">Virement</option>
                                            <option value="Autre">Autre</option>
                                        </select>
                                    </div>

                                    {/* Application intelligente des frais d'inscription selon la règle annuelle */}
                                    <div className={`p-4 rounded-xl border text-sm transition-all ${
                                        !includeRegistrationFee
                                            ? 'bg-emerald-50/90 border-emerald-200 text-emerald-950'
                                            : 'bg-amber-50/90 border-amber-200 text-amber-950'
                                    }`}>
                                        <div className="flex items-center justify-between text-xs mb-2">
                                            <span className="font-semibold text-gray-700">Prix unitaire du forfait :</span>
                                            <span className="font-bold text-gray-900">
                                                {selectedForfait ? Number(selectedForfait.price).toLocaleString() : 0} FCFA
                                            </span>
                                        </div>
                                        {quantity > 1 && (
                                            <div className="flex items-center justify-between text-xs mb-2">
                                                <span className="font-semibold text-gray-700">Sous-total ({quantity}x) :</span>
                                                <span className="font-bold text-gray-900">
                                                    {selectedForfait ? (Number(selectedForfait.price) * quantity).toLocaleString() : 0} FCFA
                                                </span>
                                            </div>
                                        )}

                                        {selectedForfait && Number(selectedForfait.registration_fee) > 0 && (
                                            <div className="pt-2 pb-2 border-t border-b border-gray-200/70 space-y-1.5 my-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            id="modal_include_reg_fee"
                                                            type="checkbox"
                                                            checked={includeRegistrationFee}
                                                            onChange={e => setIncludeRegistrationFee(e.target.checked)}
                                                            className="w-4 h-4 text-brand-primary rounded focus:ring-brand-primary cursor-pointer shrink-0"
                                                        />
                                                        <label htmlFor="modal_include_reg_fee" className="text-xs font-bold cursor-pointer select-none">
                                                            Frais d'inscription annuels ({Number(selectedForfait.registration_fee).toLocaleString()} FCFA)
                                                        </label>
                                                    </div>
                                                    <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded whitespace-nowrap shrink-0 ${
                                                        !includeRegistrationFee 
                                                            ? 'bg-emerald-200 text-emerald-900' 
                                                            : 'bg-amber-200 text-amber-900'
                                                    }`}>
                                                        {!includeRegistrationFee ? '0 FCFA (Non appliqués)' : `+ ${Number(selectedForfait.registration_fee).toLocaleString()} FCFA`}
                                                    </span>
                                                </div>

                                                <div className="text-[11px] leading-relaxed pl-6">
                                                    {!includeRegistrationFee ? (
                                                        <p className="text-emerald-800 flex items-start gap-1.5">
                                                            <ShieldCheck size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                                                            <span>
                                                                <strong>Cotisation annuelle à jour :</strong> Le client conserve le même forfait (<em>{selectedForfait.name}</em>). Les frais annuels ne sont pas facturés.
                                                            </span>
                                                        </p>
                                                    ) : isSameForfait ? (
                                                        <p className="text-amber-800 flex items-start gap-1.5">
                                                            <Info size={14} className="text-amber-600 shrink-0 mt-0.5" />
                                                            <span>
                                                                <strong>Renouvellement annuel :</strong> L'inscription annuelle remonte à plus d'un an (+{Number(selectedForfait.registration_fee).toLocaleString()} FCFA).
                                                            </span>
                                                        </p>
                                                    ) : (
                                                        <p className="text-amber-800 flex items-start gap-1.5">
                                                            <Info size={14} className="text-amber-600 shrink-0 mt-0.5" />
                                                            <span>
                                                                <strong>Changement de forfait détecté :</strong> Le client passe à un nouveau forfait (<em>{selectedForfait.name}</em>). Les frais d'inscription du nouveau forfait s'appliquent.
                                                            </span>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex justify-between font-bold pt-2 mt-1">
                                            <span className="text-sm">Montant total de l'abonnement :</span>
                                            <span className="text-lg font-black text-brand-dark">{currentPrice.toLocaleString()} FCFA</span>
                                        </div>
                                    </div>

                                    <button type="submit" className="w-full py-2.5 bg-brand-primary text-brand-dark rounded-xl font-bold hover:bg-yellow-400 transition shadow-sm cursor-pointer">
                                        Valider l'abonnement & Enregistrer le paiement
                                    </button>
                                </form>
                            )}

                            {/* --- OPTION 2 : FORFAIT SÉANCE UNIQUE --- */}
                            {serviceType === 'seance' && (
                                <form onSubmit={handleAssignSeance} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Choisir un forfait séance
                                        </label>
                                        <select 
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary font-medium bg-white"
                                            value={selectedForfaitScId || (selectedSeanceForfait ? selectedSeanceForfait.id : '')}
                                            onChange={(e) => setSelectedForfaitScId(e.target.value)}
                                            required
                                        >
                                            {forfaitsSeance.map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name} — {Number(s.price).toLocaleString()} FCFA
                                                </option>
                                            ))}
                                        </select>
                                        {selectedSeanceForfait?.description && (
                                            <p className="mt-1.5 text-xs text-gray-500 italic bg-gray-50 p-2 rounded">
                                                {selectedSeanceForfait.description}
                                            </p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Date de la séance</label>
                                            <input 
                                                type="date" 
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary" 
                                                value={seanceDate}
                                                onChange={(e) => setSeanceDate(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de séances</label>
                                            <input 
                                                type="number" min="1"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary"
                                                value={seanceQuantity}
                                                onChange={(e) => setSeanceQuantity(parseInt(e.target.value) || 1)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                                        <select 
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-brand-primary bg-white font-medium"
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            required
                                        >
                                            <option value="Espèces">Espèces</option>
                                            <option value="Wave">Wave</option>
                                            <option value="Orange Money">Orange Money</option>
                                            <option value="MTN Money">MTN Money</option>
                                            <option value="Moov Money">Moov Money</option>
                                            <option value="Carte Bancaire">Carte Bancaire</option>
                                            <option value="Virement">Virement</option>
                                            <option value="Autre">Autre</option>
                                        </select>
                                    </div>

                                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-950">
                                        <div className="flex items-center justify-between text-xs mb-2">
                                            <span className="font-semibold text-gray-700">Prix unitaire par séance :</span>
                                            <span className="font-bold text-gray-900">
                                                {selectedSeanceForfait ? Number(selectedSeanceForfait.price).toLocaleString() : 0} FCFA
                                            </span>
                                        </div>
                                        {seanceQuantity > 1 && (
                                            <div className="flex items-center justify-between text-xs mb-2">
                                                <span className="font-semibold text-gray-700">Sous-total ({seanceQuantity} séances) :</span>
                                                <span className="font-bold text-blue-950">
                                                    {(Number(selectedSeanceForfait?.price || 0) * seanceQuantity).toLocaleString()} FCFA
                                                </span>
                                            </div>
                                        )}
                                        {seanceRegFee > 0 && (
                                            <div className="flex items-center justify-between text-xs pb-2 border-b border-blue-200/60 mb-1">
                                                <span className="font-semibold text-gray-700">Frais d'inscription configurés :</span>
                                                <span className="font-bold text-blue-900 bg-blue-100 px-2 py-0.5 rounded">
                                                    + {seanceRegFee.toLocaleString()} FCFA (Automatique)
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex justify-between font-bold pt-1 border-t border-blue-200/60 mt-1">
                                            <span>Montant total de la séance :</span>
                                            <span className="text-lg font-black text-blue-900">{currentSeancePrice.toLocaleString()} FCFA</span>
                                        </div>
                                    </div>

                                    <button type="submit" className="w-full py-2.5 bg-brand-primary text-brand-dark rounded-xl font-bold hover:bg-yellow-400 transition shadow-sm cursor-pointer">
                                        Enregistrer la séance & Encaisser
                                    </button>
                                </form>
                            )}
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="space-y-4">
                            <h4 className="font-bold text-gray-800">Historique des Abonnements & Séances</h4>
                            {clientSubscriptions.length === 0 ? (
                                <p className="text-sm text-gray-500 italic">Aucun abonnement trouvé.</p>
                            ) : (
                                <div className="space-y-3">
                                    {clientSubscriptions.map((sub, i) => (
                                        <div key={i} className="border border-gray-200 p-4 rounded-md shadow-sm text-sm flex justify-between items-center">
                                            <div>
                                                <p className="font-bold text-gray-900">{sub.type_name} <span className="text-gray-500 font-normal">({sub.periodicity})</span></p>
                                                <p className="text-gray-600">Du {new Date(sub.start_date).toLocaleDateString()} au {new Date(sub.end_date).toLocaleDateString()}</p>
                                                <div className="mt-1 flex gap-4">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${sub.status === 'actif' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                                        {sub.status}
                                                    </span>
                                                    <span className="text-gray-500 text-xs">
                                                        Payé: {Number(sub.amount_paid).toLocaleString()} / {Number(sub.expected_price).toLocaleString()} FCFA
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                {extendingSubId === sub.id ? (
                                                    <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
                                                        <input 
                                                            type="number" min="1" 
                                                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                                            value={extendQuantity}
                                                            onChange={(e) => setExtendQuantity(parseInt(e.target.value) || 1)}
                                                        />
                                                        <span className="text-xs text-gray-500">x {sub.periodicity}</span>
                                                        <button 
                                                            onClick={() => handleExtend(sub)}
                                                            className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 font-semibold"
                                                        >
                                                            Valider
                                                        </button>
                                                        <button 
                                                            onClick={() => setExtendingSubId(null)}
                                                            className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                                                        >
                                                            Annuler
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button 
                                                        onClick={() => {
                                                            setExtendingSubId(sub.id);
                                                            setExtendQuantity(1);
                                                        }}
                                                        className="px-3 py-1.5 border border-brand-primary text-brand-dark bg-brand-primary/10 text-sm font-bold rounded hover:bg-brand-primary/20 transition cursor-pointer"
                                                    >
                                                        Prolonger
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}


                    {activeTab === 'reminders' && (
                        <div className="space-y-4">
                            <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-md">
                                <h4 className="font-bold text-yellow-800 text-sm mb-1">Relance Automatique (Échéance)</h4>
                                <p className="text-yellow-700 text-xs">Des alertes sont générées automatiquement avant l'expiration des abonnements.</p>
                            </div>
                            <button 
                                type="button"
                                onClick={() => alert("Notification de relance transmise au client.")}
                                className="w-full py-2 bg-gray-100 text-gray-700 rounded-md font-medium hover:bg-gray-200 transition border border-gray-200 cursor-pointer"
                            >
                                Déclencher une relance manuelle
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {receiptData && (
                <ReceiptModal
                    isOpen={!!receiptData}
                    onClose={() => setReceiptData(null)}
                    type={receiptData.type}
                    data={receiptData.data}
                />
            )}
        </div>
    );
};

export default ClientManageModal;